/**
 * 拖拽控制器
 * 处理碎片的拖拽逻辑，与渲染器和游戏逻辑解耦
 */
import { Vector2, DragState } from '../types';
import { PuzzleBoard } from '../game/PuzzleBoard';
import { PuzzlePiece } from '../game/PuzzlePiece';
import { HexMath } from '../core/HexMath';

export interface DragEvents {
    onDragStart?: (piece: PuzzlePiece, position: Vector2) => void;
    onDragMove?: (piece: PuzzlePiece, position: Vector2) => void;
    onDragEnd?: (piece: PuzzlePiece, position: Vector2, snapped: boolean) => void;
    onRotate?: (piece: PuzzlePiece, clockwise: boolean) => void;
}

export class DragController {
    private board: PuzzleBoard;
    private events: DragEvents = {};
    private state: DragState = {
        isDragging: false,
        pieceId: null,
        startPosition: { x: 0, y: 0 },
        offset: { x: 0, y: 0 }
    };

    // 拖拽历史（用于撤销）
    private dragHistory: Array<{
        pieceId: string;
        fromPosition: Vector2;
        fromRotation: number;
    }> = [];

    private maxHistoryLength: number = 50;
    private enableRotation: boolean = true;

    constructor(board: PuzzleBoard) {
        this.board = board;
    }

    /**
     * 设置事件监听
     */
    setEvents(events: DragEvents): void {
        this.events = { ...this.events, ...events };
    }

    setEnableRotation(enable: boolean): void {
        this.enableRotation = enable;
    }

    /**
     * 开始拖拽
     */
    startDrag(pieceId: string, pointerPosition: Vector2): boolean {
        const piece = this.board.getPiece(pieceId);
        if (!piece) return false;

        // 如果已归位，先取消归位状态，允许重新移动
        if (piece.isPlaced) {
            this.board.unplacePiece(piece);
        }

        // 记录历史
        this.recordHistory(piece);

        // 更新状态
        this.state = {
            isDragging: true,
            pieceId: pieceId,
            startPosition: { ...piece.position },
            offset: {
                x: piece.position.x - pointerPosition.x,
                y: piece.position.y - pointerPosition.y
            }
        };

        // 选中碎片
        this.board.selectPiece(piece);

        this.events.onDragStart?.(piece, pointerPosition);

        return true;
    }

    /**
     * 拖拽移动
     */
    moveDrag(pointerPosition: Vector2): void {
        if (!this.state.isDragging || !this.state.pieceId) return;

        const piece = this.board.getPiece(this.state.pieceId);
        if (!piece) return;

        // 计算新位置
        const newPosition: Vector2 = {
            x: pointerPosition.x + this.state.offset.x,
            y: pointerPosition.y + this.state.offset.y
        };

        // 计算位移
        const delta = HexMath.sub(newPosition, piece.position);

        // 移动碎片（包括组内其他碎片）
        this.board.movePiece(piece, delta);

        this.events.onDragMove?.(piece, newPosition);
    }

    /**
     * 结束拖拽
     */
    endDrag(pointerPosition: Vector2): boolean {
        if (!this.state.isDragging || !this.state.pieceId) return false;

        const piece = this.board.getPiece(this.state.pieceId);
        if (!piece) {
            this.resetState();
            return false;
        }

        // 检查吸附
        const result = this.board.releasePiece(piece);
        const snapped = result.success;

        this.events.onDragEnd?.(piece, piece.position, snapped);

        // 重置状态
        this.resetState();

        return snapped;
    }

    /**
     * 取消拖拽
     */
    cancelDrag(): void {
        if (!this.state.isDragging || !this.state.pieceId) return;

        const piece = this.board.getPiece(this.state.pieceId);
        if (piece) {
            // 恢复到开始位置
            piece.setPosition(this.state.startPosition);
        }

        this.resetState();
    }

    /**
     * 旋转当前选中的碎片
     */
    rotateCurrent(clockwise: boolean = true): void {
        if (!this.enableRotation) return;
        const piece = this.board.selectedPiece;
        if (!piece) return;

        if (piece.isPlaced) {
            this.board.unplacePiece(piece);
        }

        // 记录历史
        this.recordHistory(piece);

        // 旋转
        this.board.rotatePiece(piece, clockwise);

        this.events.onRotate?.(piece, clockwise);
    }

    /**
     * 根据位置旋转碎片
     */
    rotateAt(position: Vector2, clockwise: boolean = true): void {
        if (!this.enableRotation) return;
        const piece = this.board.findPieceAtPosition(position);
        console.log('rotateAt:', { position, piece: piece?.id, isPlaced: piece?.isPlaced });
        if (!piece) return;

        if (piece.isPlaced) {
            this.board.unplacePiece(piece);
        }

        // 选中并旋转
        this.board.selectPiece(piece);
        this.recordHistory(piece);

        const beforeRotation = piece.rotation;
        this.board.rotatePiece(piece, clockwise);
        console.log('Rotation changed:', beforeRotation, '->', piece.rotation);

        console.log('Calling onRotate event, hasHandler:', !!this.events.onRotate);
        this.events.onRotate?.(piece, clockwise);
    }

    /**
     * 记录历史
     */
    private recordHistory(piece: PuzzlePiece): void {
        this.dragHistory.push({
            pieceId: piece.id,
            fromPosition: { ...piece.position },
            fromRotation: piece.rotation
        });

        // 限制历史长度
        if (this.dragHistory.length > this.maxHistoryLength) {
            this.dragHistory.shift();
        }
    }

    /**
     * 撤销上一步
     */
    undo(): boolean {
        const record = this.dragHistory.pop();
        if (!record) return false;

        const piece = this.board.getPiece(record.pieceId);
        if (!piece || piece.isPlaced) return false;

        piece.setPosition(record.fromPosition);
        piece.setRotation(record.fromRotation);

        return true;
    }

    /**
     * 是否可以撤销
     */
    canUndo(): boolean {
        return this.dragHistory.length > 0;
    }

    /**
     * 清空历史
     */
    clearHistory(): void {
        this.dragHistory = [];
    }

    /**
     * 重置状态
     */
    private resetState(): void {
        this.state = {
            isDragging: false,
            pieceId: null,
            startPosition: { x: 0, y: 0 },
            offset: { x: 0, y: 0 }
        };
    }

    /**
     * 是否正在拖拽
     */
    get isDragging(): boolean {
        return this.state.isDragging;
    }

    /**
     * 获取当前拖拽的碎片 ID
     */
    get currentPieceId(): string | null {
        return this.state.pieceId;
    }

    /**
     * 销毁
     */
    destroy(): void {
        this.resetState();
        this.clearHistory();
    }
}
